import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router";

const down = keyframes`
  0%{
    opacity:0;
    transform: translateY(0) rotate(45deg);
  }
  100%{
    opacity:1;
    transform: translateY(0.75em) rotate(45deg);
  }
`;

const ScrollArrow = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: inherit;
  color: white;
  font-family: inherit;
  cursor: pointer;
  background-color: transparent;
  position: relative;
  outline: none;
  margin: 1rem 0;
  border: none;
  padding: 0;

  div {
    border-bottom: 1px solid transparent;
  }

  &:focus div {
    border-color: #fff;
  }
`;

const ChevronWrapper = styled.span`
  display: block;
  animation: ${down} 1.5s infinite;
`;

const Scroll = styled.span<{ triangleColor: string }>`
  display: block;
  width: 0.6em;
  height: 0.6em;
  border-bottom: 2px solid ${(props) => props.triangleColor};
  border-right: 2px solid ${(props) => props.triangleColor};
`;

const ScrollText = styled.div<{ textColor: string }>`
  text-transform: uppercase;
  color: ${(props) => props.textColor};
  white-space: nowrap;
`;

interface AnimatedScrollArrowProps {
  text?: string;
  triangleColor?: string;
  textColor?: string;
  hash?: string;
}

export const AnimatedScrollArrow = ({
  text = "Scroll to explore",
  triangleColor = "#c6c6c6",
  textColor = "gray",
  hash,
}: AnimatedScrollArrowProps) => (
  <AnimatedScrollArrowContent
    text={text}
    triangleColor={triangleColor}
    textColor={textColor}
    hash={hash}
  />
);

const AnimatedScrollArrowContent = ({
  text,
  triangleColor,
  textColor,
  hash,
}: Required<
  Pick<AnimatedScrollArrowProps, "text" | "triangleColor" | "textColor">
> &
  Pick<AnimatedScrollArrowProps, "hash">) => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToHash = (hashValue: string) => {
    const id = hashValue.replace(/^#/, "");
    if (!id) {
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleClick = () => {
    if (!hash) {
      return;
    }

    const targetHash = hash.startsWith("#") ? hash : `#${hash}`;

    if (location.hash !== targetHash) {
      navigate(targetHash);
    }

    requestAnimationFrame(() => scrollToHash(targetHash));
  };

  return (
    <ScrollArrow type="button" onClick={handleClick}>
      <ScrollText textColor={textColor}>{text}</ScrollText>
      <ChevronWrapper>
        <Scroll triangleColor={triangleColor} />
      </ChevronWrapper>
    </ScrollArrow>
  );
};
